import * as React from 'react';

// API
import apibridge from '../apibridge';

// PACKAGES
import { Tooltip } from 'reactstrap';

// COMPONENTS
import Button from './Button';
import StatusIcon from './StatusIcon';
import GlobalModal from './GlobalModal';
import AddDocumentModal from '../pages/StaffProfile/Modals/AddDocumentModal';
import CitedDocumentModal from '../pages/StaffProfile/Modals/CitedDocumentModal';
import DisplayDateTime from './DisplayDateTime';
import LoadingSpinner from './LoadingSpinner';

// UTILS
import { WWCCStyleDisplay, WWCCTextDisplay } from '../utils';

// ICONS
import { ReactComponent as PlusIcon } from '../assets/svg/plus.svg';
import { ReactComponent as InfoIcon } from '../assets/svg/Information.svg';

// TYPES
import {
	StaffFetchDocument,
	StaffFetchStaffMember,
	WorkingWithChildrenCheckSingleStatusCheckCommand,
	WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult
} from '../api/models';

interface ProfileCertificationsTypes {
	certifications?: Array<StaffFetchDocument>;
	defaultMaxItems?: number;
	referenceChecks?: Array<StaffFetchDocument>;
	staffId?: string;
	canUpdate?: boolean;
	didUpdate?: React.Dispatch<React.SetStateAction<boolean>>;
	getStaffProfileData: () => Promise<void>;
	isYou: boolean | undefined;
	staffProfileData?: StaffFetchStaffMember;
}

const ProfileCertifications: React.FC<ProfileCertificationsTypes> = ({
	certifications,
	defaultMaxItems,
	referenceChecks,
	staffId,
	canUpdate,
	isYou,
	didUpdate,
	getStaffProfileData,
	staffProfileData
}) => {
	const [maxCertificationsItem, setMaxCertificationsItem] = React.useState<number>(defaultMaxItems || 5);

	const [showAddDocumentModal, setShowAddDocumentModal] = React.useState<boolean>(false);

	const [showCitedDocumentModal, setShowCitedDocumentModal] = React.useState<boolean>(false);

	const [currentFormID, setCurrentFormId] = React.useState<string>('');

	const [currentFormModal, setCurrentFormModal] = React.useState<string>('');

	const [modalMounted, setModalMounted] = React.useState<boolean>(false);

	const certificationJoin = React.useMemo(() => {
		if (isYou && !canUpdate) {
			return certifications ? [...certifications] : [];
		} else if (canUpdate && certifications) {
			return referenceChecks && certifications
				? [...referenceChecks, ...certifications]
				: certifications
				? [...certifications]
				: [];
		} else {
			return referenceChecks ? [...referenceChecks] : [];
		}
	}, [isYou, canUpdate, certifications, referenceChecks]);

	React.useEffect(() => {
		setModalMounted(true);
	}, []);

	React.useEffect(() => {
		if (!showCitedDocumentModal && modalMounted) {
			didUpdate!(true);
		}
	}, []);

	const viewCertification = (item: any) => {
		if (item.displayName === 'Reference checks') {
			setCurrentFormModal(item.displayName || '');

			setCurrentFormId(item.id || '');

			setShowAddDocumentModal(true);
		} else {
			setShowCitedDocumentModal(true);

			setCurrentFormModal(item.displayName || '');

			setCurrentFormId(item.id || '');
		}
	};

	// ===== WWCC API Logic =====
	const [WWCCTooltipOpen, setWWCCTooltipOpen] = React.useState<{ [key: number]: boolean }>({});
	const toggleWWCCTooltip = (idx: number) => {
		setWWCCTooltipOpen((prev) => ({ ...prev, [idx]: !prev[idx] }));
	};

	// State to manage WWCC Status functions
	const [isLoadingWWCCStatusCheck, setIsLoadingWWCCStatusCheck] = React.useState<{ [key: string]: boolean }>({});
	const [showWWCCStatusError, setShowWWCCStatusError] = React.useState<{ [key: string]: boolean }>({});

	// Initial State for WWCC Status - now a map of certification ID to status
	const initialWWCCStatus: { [key: string]: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult } = {};

	// Main State for WWCC Status - now stores multiple statuses
	const [WWCCStatus, setWWCCStatus] = React.useState<{
		[key: string]: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult;
	}>(initialWWCCStatus);

	// Function to check WWCC Status for a specific certification
	const confirmCheckWWWCStatus = async (certificationId: string, certificationData: StaffFetchDocument) => {
		setIsLoadingWWCCStatusCheck((prev) => ({ ...prev, [certificationId]: true }));
		setShowWWCCStatusError((prev) => ({ ...prev, [certificationId]: false }));

		const submitData: WorkingWithChildrenCheckSingleStatusCheckCommand = {
			certificationReviewId: certificationId,
			state: certificationData.state || '',
			referenceNumber: certificationData.certificationNumber || '',
			surname: staffProfileData?.lastName || ''
		};

		const response = await apibridge.postWorkingwithchildrencheckSinglestatuscheck(submitData);

		if (response.isError) {
			setShowWWCCStatusError((prev) => ({ ...prev, [certificationId]: true }));
		} else {
			// Type assertion after checking isError is false
			const successResponse = response as { result?: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult };
			if (successResponse.result) {
				setWWCCStatus((prev) => ({
					...prev,
					[certificationId]: successResponse.result as WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult
				}));
			}
		}

		setIsLoadingWWCCStatusCheck((prev) => ({ ...prev, [certificationId]: false }));
	};

	// Initialize WWCCStatus with existing certification data on component mount
	React.useEffect(() => {
		if (certificationJoin) {
			const updatedStatus: { [key: string]: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult } = {};

			certificationJoin.forEach((item) => {
				if (item.certificationType === 'WorkingWithChildrenCheck' && item.id) {
					// Always update with the latest data from certificationJoin
					updatedStatus[item.id] = {
						isSuccess: false,
						eligibleToWork: item.wwccEligibleToWork,
						message: item.wwccMessage || '',
						expiryDate: '',
						checkDate: item.wwccLastChecked || ''
					};
				}
			});

			// Update WWCCStatus with the latest data, preserving any existing status checks
			setWWCCStatus((prev) => ({
				...prev,
				...updatedStatus
			}));
		}
	}, [certificationJoin]);

	return (
		<>
			{certificationJoin && certificationJoin.length > 0 && (
				<section className="info-section">
					<div className="section-header">
						<h2 className="header-name color-dark">Documents</h2>
					</div>

					{certificationJoin.slice(0, maxCertificationsItem).map((item, index) => {
						const tooltipId = `wwcc-tooltip-${index}`;

						// Extract WWCC status data for this item
						const wwccStatus = item.id ? WWCCStatus[item.id] : null;

						const eligibleToWork = wwccStatus?.eligibleToWork ?? item.wwccEligibleToWork;
						const message = wwccStatus?.message ?? item.wwccMessage;
						const lastChecked = wwccStatus?.checkDate ?? item.wwccLastChecked;

						return (
							<div key={index} className="row-container">
								<div className="row detail-row">
									<div className="col-1 col-md-auto pdl-0 pdr-0">
										<StatusIcon statusType={item.status || 'Error'} />
									</div>

									<div className="col-11 col-md-auto col-lg-9 full-width">
										<div className="row detail-info">
											<div className="col-lg-4 detail-name">
												{item.displayName}
												{item.title && ` - ${item.title}`}
											</div>

											<div className="col-lg-8 detail-status">
												<span className={`label ${item.status?.toLowerCase()}`}>{item.statusLabel}</span>

												{item.certificationType === 'WorkingWithChildrenCheck' &&
												item.wwccCanPerformSingleStatusCheck ? (
													<div className="d-flex justify-content-start flex-wrap mt-1 gap-1 flex-shrink-1">
														<div className="d-flex gap-2 align-items-center justify-content-start">
															<p className={`mb-0 body-medium fw-medium ${WWCCStyleDisplay(eligibleToWork)}`}>
																Eligible to work: {WWCCTextDisplay(eligibleToWork)}
															</p>

															{message ? (
																<>
																	<InfoIcon className="info-icon mt-0" id={tooltipId} />

																	<Tooltip
																		placement="top"
																		isOpen={!!WWCCTooltipOpen[index]}
																		autohide={false}
																		target={tooltipId}
																		toggle={() => toggleWWCCTooltip(index)}
																	>
																		<div className="tooltip-content">{message}</div>
																	</Tooltip>
																</>
															) : null}
														</div>

														<div className="d-flex align-items-center gap-2">
															<>
																<p className="body-medium text-grey-4 mb-0">Last checked:</p>

																<p className={`body-medium ml-1 ${WWCCStyleDisplay(eligibleToWork)}`}>
																	{lastChecked ? (
																		<DisplayDateTime format="DD/MM/YYYY, hh:mma">{lastChecked}</DisplayDateTime>
																	) : (
																		<>Not checked yet</>
																	)}
																</p>
															</>
														</div>
													</div>
												) : null}
											</div>
										</div>
									</div>

									<div className="link-group pr-0 col-auto">
										{item.certificationType === 'WorkingWithChildrenCheck' && item.wwccCanPerformSingleStatusCheck && (
											<div className="check-status-wrapper mr-2">
												{isLoadingWWCCStatusCheck[item.id || ''] ? (
													<div className="d-flex align-items-center gap-2">
														<span className="text-grey-5 font-weight-bold">Processing...</span>

														<LoadingSpinner show={true} isStatic width={18} height={18} />
													</div>
												) : (
													<button
														className="btn-secondary btn-check-status"
														onClick={() => item.id && confirmCheckWWWCStatus(item.id, item)}
													>
														Check Status
													</button>
												)}
											</div>
										)}
										{canUpdate && item.status === 'Error' && item.displayName === 'Reference checks' && !isYou ? (
											<Button
												className="btn-text pr-0 mt-0"
												type="button"
												onClick={() => {
													setShowAddDocumentModal(true);
													setCurrentFormModal(item.displayName || '');
													setCurrentFormId('');
												}}
											>
												Add
											</Button>
										) : item.displayName === 'Reference checks' && isYou ? null : (
											<Button
												className="btn-text pr-0 mt-0"
												type="button"
												onClick={() => {
													viewCertification(item);
												}}
											>
												View
											</Button>
										)}
									</div>

									{showWWCCStatusError[item.id || ''] && (
										<div className="col-12 pdr-0">
											<div className="d-flex flex-column align-items-end">
												<p className="body-small fw-medium text-alert-warning mt-2 mb-0 text-right">
													Something went wrong.
												</p>
												<p className="body-small fw-medium text-alert-warning mb-0 text-right">
													Please try again in a few minutes
												</p>
											</div>
										</div>
									)}
								</div>
							</div>
						);
					})}

					{certificationJoin && certificationJoin.length > 0 && maxCertificationsItem < certificationJoin.length && (
						<Button
							className="btn-secondary btn-show-more"
							onClick={() => setMaxCertificationsItem(certificationJoin.length)}
							buttonIcon={<PlusIcon className="link-icon" />}
						>
							Show {certificationJoin.length - maxCertificationsItem} more
						</Button>
					)}
				</section>
			)}

			<GlobalModal titleText={currentFormModal} isOpen={showAddDocumentModal} setIsOpen={setShowAddDocumentModal}>
				<AddDocumentModal
					formType={currentFormModal}
					staffId={staffId}
					getStaffProfileData={getStaffProfileData}
					currentFormID={currentFormID}
				/>
			</GlobalModal>

			<GlobalModal
				isOpen={showCitedDocumentModal}
				titleText={currentFormModal}
				setIsOpen={setShowCitedDocumentModal}
				functionOnClose={getStaffProfileData}
			>
				<CitedDocumentModal
					update={getStaffProfileData}
					canUpdate={canUpdate}
					isYou={isYou}
					setTitle={setCurrentFormModal}
					formId={currentFormID}
					staffProfileData={staffProfileData}
				/>
			</GlobalModal>
		</>
	);
};

export default ProfileCertifications;
